import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  TemplateRef,
  TrackByFunction,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { ConnectedPosition, OverlayModule, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of, isObservable, combineLatest, BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';

export type DisplayWithFn<T> = (value: T) => string;

@Component({
  standalone: true,
  selector: 'app-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  styleUrls: ['./input-autocomplete.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, OverlayModule, TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputAutocompleteComponent),
      multi: true,
    },
  ],
})
export class InputAutocompleteComponent<T = unknown> implements OnInit, ControlValueAccessor {
  @Input() placeholder = '';
  @Input() label = '';

  @Input()
  set options(value: T[] | Observable<T[]>) {
    this._optionsInput$.next(isObservable(value) ? value : of(value || []));
  }
  private readonly _optionsInput$ = new BehaviorSubject<Observable<T[]>>(of([]));
  private readonly _optionsSource$: Observable<T[]> = this._optionsInput$.pipe(
    switchMap(source => source),
  );

  @Input() displayWith?: DisplayWithFn<T>;
  @Input() debounceMs = 300;
  @Input() minLength = 1;
  @Input() freeText = false;
  @Input() optionTemplate?: TemplateRef<{ $implicit: T }>;
  @Input() trackBy: TrackByFunction<T> = (index: number) => index;

  @Output() optionSelected = new EventEmitter<T | string>();
  @Output() inputChange = new EventEmitter<string>();

  public readonly searchControl = new FormControl<string>('', { nonNullable: true });
  public filteredOptions$!: Observable<T[]>;
  public isOpen = false;
  public selectedValue: T | string | null = null;
  public triggerWidth = 0;

  @ViewChild('trigger', { static: true }) private triggerEl!: ElementRef<HTMLElement>;
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  private readonly scrollStrategies = inject(ScrollStrategyOptions);
  protected readonly scrollStrategy = this.scrollStrategies.reposition();

  protected readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  private onChange: (value: T | string | null) => void = () => {
    /* empty */
  };
  private onTouched: () => void = () => {
    /* empty */
  };

  ngOnInit(): void {
    const input$ = this.searchControl.valueChanges.pipe(
      debounceTime(this.debounceMs),
      startWith(this.searchControl.value),
      distinctUntilChanged(),
    );

    this.filteredOptions$ = combineLatest([input$, this._optionsSource$]).pipe(
      map(([query, options]) => {
        this.inputChange.emit(query);

        if (query.length > 0 && query.length < this.minLength) return [];

        const list = options || [];
        if (!query) return list;

        const lower = query.toLowerCase();
        return list.filter(opt => this.displayLabel(opt).toLowerCase().includes(lower));
      }),
    );
  }

  protected displayLabel(value: string | T | null): string {
    if (value == null) return '';
    if (this.displayWith) return this.displayWith(value as T) ?? '';
    return String(value);
  }

  public toggleOpen(): void {
    if (this.searchControl.disabled) return;
    if (this.isOpen) this.close();
    else this.open();
  }

  public open(): void {
    if (this.isOpen || this.searchControl.disabled) return;
    this.syncTriggerWidth();
    this.isOpen = true;
    this.searchControl.setValue('');
  }

  private syncTriggerWidth(): void {
    this.triggerWidth = this.triggerEl.nativeElement.offsetWidth;
  }

  public close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.onTouched();
  }

  public onOverlayAttached(): void {
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  public selectOption(option: T): void {
    this.selectedValue = option;
    this.optionSelected.emit(option);
    this.onChange(option);
    this.close();
  }

  public onInputKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || !this.freeText) return;

    const val = this.searchControl.value;
    if (!val || val.length < this.minLength) return;

    this.selectedValue = val;
    this.optionSelected.emit(val);
    this.onChange(val);
    this.close();
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (this.isOpen) this.close();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    if (this.isOpen) this.syncTriggerWidth();
  }

  public writeValue(value: T | string | null): void {
    this.selectedValue = value;
  }

  public registerOnChange(fn: (value: T | string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.searchControl.disable({ emitEvent: false });
    else this.searchControl.enable({ emitEvent: false });
  }
}
